import React, { useContext, useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  CircularProgress,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../hooks";
import { showSnackbar } from "../../reducers/snackbarSlice";
import { AuthContext } from "../../providers/AuthProvider";
import { SecurityLevel } from "../../types/User";
import { listAdmins, createAdmin, setSecurityScore, PlatformAdmin } from "./PlatformAdminsRepository";

const PlatformAdminsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);
  const [admins, setAdmins] = useState<PlatformAdmin[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [fullName, setFullName] = useState<string>("");
  const [email, setEmail] = useState<string>("");

  const refresh = React.useCallback(() => {
    return listAdmins()
      .then(setAdmins)
      .catch((error) => {
        console.error(error);
        dispatch(showSnackbar({ message: t("admins.load_failed"), severity: "error" }));
      });
  }, [dispatch, t]);

  useEffect(() => {
    let active = true;
    refresh().finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [refresh]);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await createAdmin({ fullName, email });
      dispatch(showSnackbar({ message: t("admins.created", { email }), severity: "success" }));
      setCreateOpen(false);
      setFullName("");
      setEmail("");
      await refresh();
    } catch (error) {
      console.error(error);
      dispatch(showSnackbar({ message: t("admins.create_failed"), severity: "error" }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetScore = async (admin: PlatformAdmin, score: number) => {
    try {
      await setSecurityScore(admin.id, score);
      dispatch(showSnackbar({ message: t("admins.score_updated"), severity: "success" }));
      await refresh();
    } catch (error) {
      console.error(error);
      dispatch(showSnackbar({ message: t("admins.score_failed"), severity: "error" }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const labelFor = (level: number) =>
    level >= SecurityLevel.PlatformAdmin ? t("admins.platform_admin") : t("admins.company_admin");

  return (
    <Box data-cy="admins-page">
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">{t("admins.title")}</Typography>
        <Button variant="contained" onClick={() => setCreateOpen(true)} data-cy="new-admin">
          {t("admins.new_admin")}
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table data-cy="admins-table">
          <TableHead>
            <TableRow>
              <TableCell>{t("admins.name")}</TableCell>
              <TableCell>{t("admins.email")}</TableCell>
              <TableCell>{t("admins.company")}</TableCell>
              <TableCell>{t("admins.security_level")}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>{t("admins.no_admins")}</TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => {
                const isSelf = user?.userId === admin.id;
                const isPlatform = admin.securityLevel >= SecurityLevel.PlatformAdmin;
                return (
                  <TableRow key={admin.id} data-cy="admin-row">
                    <TableCell>{admin.fullName}</TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>{admin.companyName ?? t("admins.no_company")}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={isPlatform ? "primary" : "default"}
                        label={labelFor(admin.securityLevel)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {isPlatform ? (
                        <Button
                          size="small"
                          disabled={isSelf}
                          onClick={() => handleSetScore(admin, SecurityLevel.CompanyAdmin)}
                          data-cy="demote-admin"
                        >
                          {t("admins.demote")}
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          onClick={() => handleSetScore(admin, SecurityLevel.PlatformAdmin)}
                          data-cy="promote-admin"
                        >
                          {t("admins.promote")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("admins.create_title")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t("admins.name_label")}
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              data-cy="admin-name"
            />
            <TextField
              label={t("admins.email_label")}
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-cy="admin-email"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t("generic.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!fullName || !email || submitting}
            data-cy="submit-admin"
          >
            {t("generic.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlatformAdminsPage;
